import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Image,
  Row,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { EmotionDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface EmotionGeneralTabProps {
  record: EmotionDetailOut;
}

export const EmotionGeneralTab = ({ record }: EmotionGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(record.id),
    },
    {
      key: 'name',
      label: t('user_catalog.emotions.fields.name'),
      children: orDash(record.name),
    },
    {
      key: 'slug',
      label: t('user_catalog.emotions.fields.slug'),
      children: orDash(record.slug && <Text code>{record.slug}</Text>),
    },
    {
      key: 'kind',
      label: t('user_catalog.emotions.fields.kind'),
      children: orDash(t(`user_catalog.emotions.kind.${record.kind}`)),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const images: { key: string; label: string; url?: string | null }[] = [
    {
      key: 'icon_url',
      label: t('user_catalog.emotions.fields.icon_url'),
      url: record.icon_url,
    },
    {
      key: 'image_url',
      label: t('user_catalog.emotions.fields.image_url'),
      url: record.image_url,
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Card title={t('base.sections.basic_info')}>
          <Descriptions
            {...verticalDescProps}
            column={{ xs: 1, sm: 2, md: 3 }}
            items={items}
          />
        </Card>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          {images.map((image) => (
            <Card key={image.key} title={image.label}>
              {image.url ? (
                <Image
                  src={image.url}
                  width="100%"
                  style={{
                    objectFit: 'cover',
                    borderRadius: token.borderRadius,
                  }}
                />
              ) : (
                orDash(null)
              )}
            </Card>
          ))}
        </Flex>
      </Col>
    </Row>
  );
};
